import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import {
  ZoomIn, ZoomOut, ExternalLink, X, Download, Printer, AlertTriangle, File,
  Loader2, ChevronLeft, ChevronRight, SplitSquareHorizontal, ArrowLeftRight, Search,
  Link2, Link2Off, RefreshCw
} from 'lucide-react';
import { getFileTypeInfo, API_URL, getToken } from './constants';
import { cn } from '../../../lib/utils';

// Cache LRU module-level pour des re-ouvertures instantanees (max 30 documents)
// Structure : { blob, version } — version = "<size>-<mtime>" renvoye par le backend
const PREVIEW_CACHE = new Map();
const PREVIEW_CACHE_MAX = 30;
const cacheBlob = (key, blob, version = null) => {
  if (PREVIEW_CACHE.has(key)) PREVIEW_CACHE.delete(key);
  PREVIEW_CACHE.set(key, { blob, version });
  while (PREVIEW_CACHE.size > PREVIEW_CACHE_MAX) {
    PREVIEW_CACHE.delete(PREVIEW_CACHE.keys().next().value);
  }
};

// Verifie si une version distante differe de la version cachee (fresh check pour 'MAJ disponible')
const checkRemoteVersion = async (doc, cacheKey) => {
  try {
    const token = getToken();
    const params = doc.souscription_id
      ? `sub_id=${encodeURIComponent(doc.souscription_id)}`
      : `ged_id=${encodeURIComponent(doc.id)}`;
    const res = await fetch(`${API_URL}/api/ged/version-check?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data[cacheKey] || null;
  } catch {
    return null;
  }
};

// Sous-composant : un panneau document (PDF/image) avec son propre fetch + cache + abort
const DocPane = ({ doc, sideLabel, onClose, onSwap, onDownload, canClose = false, canSwap = false, pageAnchor = null }) => {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [stale, setStale] = useState(false);  // MAJ detectee : badge "MAJ disponible"
  const [refreshTick, setRefreshTick] = useState(0);  // force re-fetch quand on clique "Recharger"
  const abortRef = useRef(null);
  const fileInfo = getFileTypeInfo(doc.original_filename || doc.filename, doc.mime_type);

  const cacheKey = doc.souscription_id ? `sub:${doc.souscription_id}` : `ged:${doc.id}`;

  useEffect(() => {
    let cancelled = false;
    if (abortRef.current) { try { abortRef.current.abort(); } catch {} }
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true); setLoadError(false); setErrorMessage(''); setBlobUrl(null); setProgress(0);
    setStale(false);

    const cached = PREVIEW_CACHE.get(cacheKey);
    if (cached && refreshTick === 0) {
      PREVIEW_CACHE.delete(cacheKey); PREVIEW_CACHE.set(cacheKey, cached);
      setBlobUrl(URL.createObjectURL(cached.blob));
      setLoading(false); setProgress(100);
      // Verification en arriere-plan : version distante vs version cachee
      if (cached.version) {
        checkRemoteVersion(doc, cacheKey).then((remoteVersion) => {
          if (cancelled) return;
          if (remoteVersion && remoteVersion !== cached.version) {
            setStale(true);
          }
        });
      }
      return () => { cancelled = true; try { controller.abort(); } catch {} };
    }
    (async () => {
      try {
        const token = getToken();
        const url = doc.souscription_id
          ? `${API_URL}/api/souscriptions-rcd/${doc.souscription_id}/documents/rcds_unique`
          : `${API_URL}/api/ged/download/${doc.id}?mode=view`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal, cache: 'no-store',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const total = parseInt(res.headers.get('Content-Length') || res.headers.get('X-File-Size') || '0', 10);
        const ct = res.headers.get('Content-Type') || 'application/pdf';
        // Capture ETag pour version (format "<size>-<mtime>" sans guillemets)
        const etag = (res.headers.get('ETag') || '').replace(/"/g, '');
        let blob;
        if (total > 0 && res.body && res.body.getReader) {
          const reader = res.body.getReader();
          const chunks = []; let received = 0;
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            chunks.push(value); received += value.length;
            if (!cancelled) setProgress(Math.min(99, Math.round((received / total) * 100)));
          }
          blob = new Blob(chunks, { type: ct });
        } else {
          blob = await res.blob();
          if (!blob.type || blob.type === 'application/octet-stream') blob = new Blob([blob], { type: ct });
        }
        if (cancelled) return;
        cacheBlob(cacheKey, blob, etag || null);
        setBlobUrl(URL.createObjectURL(blob));
        setProgress(100); setLoading(false);
      } catch (err) {
        if (cancelled || err?.name === 'AbortError') return;
        setErrorMessage(String(err?.message || err || '').replace(/^Error:\s*/, ''));
        setLoadError(true); setLoading(false);
      }
    })();
    return () => { cancelled = true; try { controller.abort(); } catch {} };
  }, [doc.id, doc.souscription_id, refreshTick]);

  useEffect(() => {
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [blobUrl]);

  const handleReloadFresh = () => {
    // Invalide le cache et force un re-fetch reseau
    PREVIEW_CACHE.delete(cacheKey);
    setStale(false);
    setRefreshTick((t) => t + 1);
  };

  const docName = doc.original_filename || doc.filename || doc.nom;
  const openInNewTab = () => {
    if (blobUrl) window.open(blobUrl, '_blank');
    else {
      const token = getToken();
      window.open(`${API_URL}/api/ged/download/${doc.id}?token=${token}&mode=view`, '_blank');
    }
  };

  return (
    <div className="flex flex-col flex-1 min-w-0 bg-gray-900 border-r last:border-r-0 border-gray-700/50" data-testid={`doc-pane-${sideLabel}`}>
      {/* Sub-header per panneau */}
      <div className="px-3 py-2 bg-gray-800/95 border-b border-gray-700/40 flex items-center gap-2 min-h-[44px]">
        <Badge variant="outline" className="border-amber-500/40 text-amber-300 text-[10px] uppercase tracking-wide font-bold">
          {sideLabel === 'left' ? 'Document A' : 'Document B'}
        </Badge>
        <div className={cn("w-6 h-6 rounded flex items-center justify-center flex-shrink-0", fileInfo.bg)}>
          <fileInfo.icon className={cn("w-3.5 h-3.5", fileInfo.color)} />
        </div>
        <p className="text-xs text-white font-medium truncate flex-1" title={docName}>{docName}</p>
        {stale && (
          <button
            type="button"
            onClick={handleReloadFresh}
            className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/40 text-amber-200 text-[10px] font-semibold uppercase tracking-wider transition-colors flex-shrink-0 animate-pulse"
            title="Le document a ete mis a jour par un administrateur. Cliquez pour recharger la derniere version."
            data-testid={`stale-badge-${sideLabel}`}
          >
            <RefreshCw className="w-3 h-3" />
            <span className="hidden sm:inline">MAJ disponible</span>
          </button>
        )}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-white" onClick={openInNewTab} title="Ouvrir dans un onglet">
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-white" onClick={() => onDownload(doc)} title="Telecharger">
            <Download className="w-3.5 h-3.5" />
          </Button>
          {canSwap && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-white" onClick={onSwap} title="Inverser les panneaux">
              <ArrowLeftRight className="w-3.5 h-3.5" />
            </Button>
          )}
          {canClose && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-red-300 hover:bg-red-900/40" onClick={onClose} title="Fermer ce panneau" data-testid={`pane-close-${sideLabel}`}>
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
      {/* Pane content */}
      <div className="flex-1 overflow-auto relative bg-gray-900 flex items-center justify-center p-2">
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
            <div className="flex flex-col items-center gap-2 px-4 py-3 rounded-xl bg-black/40 backdrop-blur-md border border-white/10">
              <div className="w-9 h-9 border-2 border-white/15 border-t-amber-500 rounded-full animate-spin" />
              <p className="text-white/90 text-xs font-medium">Chargement...</p>
              {progress > 0 && (
                <div className="w-32">
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-all duration-150" style={{ width: `${progress}%` }} />
                  </div>
                  <p className="text-[9px] text-white/50 text-center mt-0.5 tabular-nums">{progress}%</p>
                </div>
              )}
            </div>
          </div>
        )}
        {loadError ? (
          <div className="text-center text-white max-w-xs">
            <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-400" />
            <p className="text-sm font-medium mb-1">Erreur de chargement</p>
            {errorMessage && <p className="text-[10px] text-amber-300/80 mb-3 font-mono break-all">{errorMessage}</p>}
            <Button onClick={() => onDownload(doc)} size="sm" className="bg-amber-600 hover:bg-amber-700">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Telecharger
            </Button>
          </div>
        ) : blobUrl && fileInfo.type === 'pdf' ? (
          <iframe
            src={`${blobUrl}#toolbar=1&navpanes=0${pageAnchor ? `&page=${pageAnchor}` : ''}`}
            key={pageAnchor || 'default'}
            className="w-full h-full rounded bg-white"
            title={docName}
            data-testid={`pdf-viewer-iframe-${sideLabel}`}
          />
        ) : blobUrl && fileInfo.type === 'image' ? (
          <img
            src={blobUrl}
            alt={docName}
            className="max-w-full max-h-full object-contain rounded shadow-2xl"
            data-testid={`image-viewer-${sideLabel}`}
          />
        ) : !loading && !loadError ? (
          <div className="text-center text-white">
            <File className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Apercu non disponible</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const DocumentPreviewModal = ({ doc, onClose, onDownload, documents, onNavigate }) => {
  const [zoom, setZoom] = useState(100);
  const [printing, setPrinting] = useState(false);
  const [compareDoc, setCompareDoc] = useState(null);  // 2e doc en mode split
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  // Mode "scroll synchronise" (split-view) : sync_page applique a chaque iframe via URL hash
  const [syncEnabled, setSyncEnabled] = useState(true);
  const [syncPage, setSyncPage] = useState(1);
  const fileInfo = getFileTypeInfo(doc.original_filename || doc.filename, doc.mime_type);

  // Navigation index
  const currentIndex = documents ? documents.findIndex(d => d.id === doc.id) : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = documents && currentIndex < documents.length - 1;

  // Liste candidate pour la comparaison (exclut le doc courant et un eventuel compareDoc)
  const compareCandidates = useMemo(() => {
    if (!documents) return [];
    const q = pickerQuery.trim().toLowerCase();
    return documents.filter(d => d.id !== doc.id && d.id !== compareDoc?.id).filter(d => {
      if (!q) return true;
      const nm = (d.original_filename || d.filename || d.nom || '').toLowerCase();
      return nm.includes(q);
    }).slice(0, 50);
  }, [documents, doc.id, compareDoc?.id, pickerQuery]);

  // Fermer le picker quand on ferme la modale ou change de doc principal
  useEffect(() => { setPickerOpen(false); }, [doc.id]);

  // Reset la pagination synchronisee quand on (sort/entre) du mode compare
  useEffect(() => { setSyncPage(1); }, [compareDoc?.id]);

  // Keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      if (pickerOpen) { setPickerOpen(false); return; }
      if (compareDoc) { setCompareDoc(null); return; }
      onClose();
      return;
    }
    // En split-view + sync : PageUp/PageDown ou Ctrl+Arrow pour pagination synchronisee
    if (compareDoc && syncEnabled) {
      if (e.key === 'PageDown' || (e.ctrlKey && e.key === 'ArrowRight')) {
        e.preventDefault();
        setSyncPage(p => p + 1);
        return;
      }
      if (e.key === 'PageUp' || (e.ctrlKey && e.key === 'ArrowLeft')) {
        e.preventDefault();
        setSyncPage(p => Math.max(1, p - 1));
        return;
      }
    }
    if (compareDoc) return;  // En split-view, on desactive arrows/zoom standards
    if (e.key === 'ArrowLeft' && hasPrev && onNavigate) onNavigate(documents[currentIndex - 1]);
    if (e.key === 'ArrowRight' && hasNext && onNavigate) onNavigate(documents[currentIndex + 1]);
    if (e.key === '+' || e.key === '=') setZoom(z => Math.min(300, z + 25));
    if (e.key === '-') setZoom(z => Math.max(25, z - 25));
  }, [onClose, hasPrev, hasNext, onNavigate, documents, currentIndex, compareDoc, pickerOpen, syncEnabled]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const docName = doc.original_filename || doc.filename || doc.nom;

  const handlePrint = async () => {
    const url = await previewCacheUrl(doc);
    if (!url) return;
    setPrinting(true);
    try {
      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-10000px;left:-10000px;width:1px;height:1px;opacity:0;pointer-events:none;';
      document.body.appendChild(iframe);
      iframe.onload = () => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
        catch { window.open(url, '_blank'); }
        setTimeout(() => { document.body.removeChild(iframe); setPrinting(false); }, 1000);
      };
      iframe.onerror = () => { document.body.removeChild(iframe); setPrinting(false); window.open(url, '_blank'); };
      iframe.src = url;
    } catch { setPrinting(false); }
  };

  // Utilitaire : recupere une URL blob depuis le cache (utilise pour print)
  const previewCacheUrl = async (d) => {
    const key = d.souscription_id ? `sub:${d.souscription_id}` : `ged:${d.id}`;
    if (PREVIEW_CACHE.has(key)) return URL.createObjectURL(PREVIEW_CACHE.get(key));
    return null;
  };

  const openInNewTab = async () => {
    const url = await previewCacheUrl(doc);
    if (url) window.open(url, '_blank');
    else {
      const token = getToken();
      window.open(`${API_URL}/api/ged/download/${doc.id}?token=${token}&mode=view`, '_blank');
    }
  };

  const handleSwapPanes = () => {
    if (!compareDoc) return;
    const newLeft = compareDoc;
    const newRight = doc;
    setCompareDoc(newRight);
    if (onNavigate) onNavigate(newLeft);
  };

  return (
    <div className="fixed inset-0 bg-black/95 flex flex-col z-50" data-testid="doc-preview-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-900/95 text-white border-b border-gray-700/50 backdrop-blur-sm">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {documents && documents.length > 1 && !compareDoc && (
            <div className="flex items-center gap-1 mr-1">
              <Button
                variant="ghost" size="icon"
                className="h-7 w-7 text-gray-400 hover:text-white disabled:opacity-30"
                disabled={!hasPrev}
                onClick={() => hasPrev && onNavigate && onNavigate(documents[currentIndex - 1])}
                data-testid="preview-nav-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-[10px] text-gray-500 tabular-nums">{currentIndex + 1}/{documents.length}</span>
              <Button
                variant="ghost" size="icon"
                className="h-7 w-7 text-gray-400 hover:text-white disabled:opacity-30"
                disabled={!hasNext}
                onClick={() => hasNext && onNavigate && onNavigate(documents[currentIndex + 1])}
                data-testid="preview-nav-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
          <div className={cn("w-8 h-8 rounded flex items-center justify-center flex-shrink-0", fileInfo.bg)}>
            <fileInfo.icon className={cn("w-4 h-4", fileInfo.color)} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">
              {compareDoc ? <span className="text-amber-300">Mode comparaison</span> : docName}
            </p>
            <p className="text-xs text-gray-400">
              {compareDoc ? `Document A vs Document B` : fileInfo.label}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {fileInfo.type === 'image' && !compareDoc && (
            <>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-300 hover:text-white" onClick={() => setZoom(z => Math.max(25, z - 25))} title="Zoom -">
                <ZoomOut className="w-4 h-4" />
              </Button>
              <span className="text-xs text-gray-400 w-12 text-center tabular-nums">{zoom}%</span>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-300 hover:text-white" onClick={() => setZoom(z => Math.min(300, z + 25))} title="Zoom +">
                <ZoomIn className="w-4 h-4" />
              </Button>
              <div className="w-px h-5 bg-gray-700 mx-1" />
            </>
          )}
          {/* Bouton Comparer (split-view) */}
          {documents && documents.length > 1 && (
            <div className="relative">
              <Button
                variant="ghost" size="sm"
                className={cn("text-gray-300 hover:text-white gap-1.5 h-8", compareDoc && "bg-amber-600/20 text-amber-200 hover:bg-amber-600/30")}
                onClick={() => {
                  if (compareDoc) { setCompareDoc(null); }
                  else { setPickerOpen(v => !v); }
                }}
                title={compareDoc ? "Quitter le mode comparaison" : "Comparer avec un autre document"}
                data-testid="preview-compare-btn"
              >
                <SplitSquareHorizontal className="w-4 h-4" />
                <span className="hidden sm:inline text-xs">{compareDoc ? 'Quitter compare' : 'Comparer'}</span>
              </Button>
              {pickerOpen && !compareDoc && (
                <div className="absolute right-0 top-full mt-1 w-80 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-50 overflow-hidden" data-testid="compare-picker">
                  <div className="p-2 border-b border-gray-700 flex items-center gap-2">
                    <Search className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      autoFocus
                      placeholder="Rechercher un document..."
                      value={pickerQuery}
                      onChange={(e) => setPickerQuery(e.target.value)}
                      className="bg-transparent text-white text-xs flex-1 outline-none placeholder-gray-500"
                      data-testid="compare-picker-search"
                    />
                  </div>
                  <div className="max-h-72 overflow-y-auto py-1">
                    {compareCandidates.length === 0 ? (
                      <p className="text-xs text-gray-500 px-3 py-4 text-center">Aucun autre document disponible</p>
                    ) : compareCandidates.map((d) => {
                      const fi = getFileTypeInfo(d.original_filename || d.filename, d.mime_type);
                      return (
                        <button
                          key={d.id}
                          className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-gray-800 transition-colors text-left"
                          onClick={() => { setCompareDoc(d); setPickerOpen(false); setPickerQuery(''); }}
                          data-testid={`compare-pick-${d.id}`}
                        >
                          <div className={cn("w-5 h-5 rounded flex items-center justify-center flex-shrink-0", fi.bg)}>
                            <fi.icon className={cn("w-3 h-3", fi.color)} />
                          </div>
                          <span className="text-xs text-white truncate flex-1" title={d.original_filename || d.filename || d.nom}>
                            {d.original_filename || d.filename || d.nom}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          {!compareDoc && (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-300 hover:text-white" onClick={openInNewTab} title="Ouvrir dans un nouvel onglet" data-testid="preview-newtab-btn">
              <ExternalLink className="w-4 h-4" />
            </Button>
          )}
          {!compareDoc && (
            <Button
              variant="ghost" size="sm"
              className="text-gray-300 hover:text-white gap-1.5 h-8"
              onClick={handlePrint}
              disabled={printing}
              title="Imprimer"
              data-testid="preview-print-btn"
            >
              {printing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
              <span className="hidden sm:inline text-xs">Imprimer</span>
            </Button>
          )}
          {!compareDoc && (
            <Button variant="ghost" size="sm" className="text-gray-300 hover:text-white gap-1.5 h-8" onClick={() => onDownload(doc)} data-testid="preview-download-btn">
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline text-xs">Telecharger</span>
            </Button>
          )}
          <div className="w-px h-5 bg-gray-700 mx-1" />
          <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-300 hover:text-white hover:bg-red-900/50" onClick={onClose} title="Fermer (Echap)" data-testid="preview-close-btn">
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Toolbar synchronisation (visible uniquement en mode comparaison PDF) */}
      {compareDoc && (
        <div className="px-3 py-2 bg-amber-900/20 border-b border-amber-700/30 flex items-center gap-3 text-amber-100" data-testid="sync-toolbar">
          <Button
            variant="ghost" size="sm"
            className={cn("h-7 gap-1.5 text-xs", syncEnabled ? "bg-amber-600/30 text-amber-100 hover:bg-amber-600/40" : "text-amber-200/60 hover:text-amber-100")}
            onClick={() => setSyncEnabled(v => !v)}
            title={syncEnabled ? "Desactiver le scroll synchronise" : "Activer le scroll synchronise"}
            data-testid="sync-toggle-btn"
          >
            {syncEnabled ? <Link2 className="w-3.5 h-3.5" /> : <Link2Off className="w-3.5 h-3.5" />}
            <span>Scroll synchro {syncEnabled ? 'ON' : 'OFF'}</span>
          </Button>
          {syncEnabled && (
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost" size="icon"
                className="h-7 w-7 text-amber-200 hover:text-white disabled:opacity-30"
                disabled={syncPage <= 1}
                onClick={() => setSyncPage(p => Math.max(1, p - 1))}
                title="Page precedente (Ctrl+gauche)"
                data-testid="sync-page-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-1 px-2 py-0.5 bg-black/30 rounded">
                <span className="text-[10px] uppercase text-amber-200/70 tracking-wide">Page</span>
                <input
                  type="number" min={1} value={syncPage}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v) && v >= 1) setSyncPage(v);
                  }}
                  className="w-12 bg-transparent text-center text-xs text-white font-semibold tabular-nums outline-none"
                  data-testid="sync-page-input"
                />
              </div>
              <Button
                variant="ghost" size="icon"
                className="h-7 w-7 text-amber-200 hover:text-white"
                onClick={() => setSyncPage(p => p + 1)}
                title="Page suivante (Ctrl+droite)"
                data-testid="sync-page-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}
          <span className="text-[10px] text-amber-200/60 ml-auto hidden md:inline">
            Raccourcis : <kbd className="px-1 bg-black/30 rounded">PageUp</kbd>/<kbd className="px-1 bg-black/30 rounded">PageDown</kbd> ou <kbd className="px-1 bg-black/30 rounded">Ctrl</kbd>+<kbd className="px-1 bg-black/30 rounded">←/→</kbd>
          </span>
        </div>
      )}

      {/* Content : panel(s) PDF */}
      <div className={cn("flex-1 overflow-hidden flex", compareDoc ? "flex-row" : "flex-col")}>
        <DocPane
          doc={doc}
          sideLabel="left"
          onDownload={onDownload}
          canSwap={!!compareDoc}
          onSwap={handleSwapPanes}
          pageAnchor={compareDoc && syncEnabled ? syncPage : null}
        />
        {compareDoc && (
          <DocPane
            doc={compareDoc}
            sideLabel="right"
            onDownload={onDownload}
            onClose={() => setCompareDoc(null)}
            canClose
            canSwap
            onSwap={handleSwapPanes}
            pageAnchor={syncEnabled ? syncPage : null}
          />
        )}
      </div>

      {/* Footer info bar */}
      <div className="bg-gray-900/95 border-t border-gray-700/50 px-4 py-1.5 flex items-center justify-between text-[11px] text-gray-500">
        <span>{compareDoc ? `Mode comparaison cote-a-cote${syncEnabled ? ` - Sync page ${syncPage}` : ''}` : docName}</span>
        <div className="flex items-center gap-3">
          {!compareDoc && <span>{fileInfo.label}</span>}
          <span className="text-gray-600">Echap : {compareDoc ? 'quitter comparaison' : 'fermer'}</span>
          {documents && documents.length > 1 && !compareDoc && <span className="text-gray-600">Fleches pour naviguer</span>}
          {compareDoc && syncEnabled && <span className="text-amber-300/60">PageUp/Down pour pagination</span>}
        </div>
      </div>
    </div>
  );
};

export default DocumentPreviewModal;
